import { useState, useRef } from "react"
import { useQuery } from "@apollo/client"

import { 
  Button, 
  Chip, 
  Pagination 
} from "@heroui/react"

import { 
  Divider, 
  ErrorPlaceholder,
  LoadingPlaceholder, 
  PaginatorInfo, 
  SimplePlaceholder, 
  SearchInput 
} from "@components"

import { PresentationChartLineIcon } from "@heroicons/react/24/outline"

import { 
  ComputerDesktopIcon, 
  DeviceTabletIcon, 
  WindowIcon 
} from "@heroicons/react/24/solid"

import { GET_DASHBOARD_EVENT_VIEWS } from "@graphql"
import { humanReadableDuration } from "@helpers"
import PropTypes from "prop-types"

const EventViewsList = ({ eventId, filter = null, endSlot = null, teacherId = null }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const searchInputRef = useRef(null)

  const { loading, error, data } = useQuery(GET_DASHBOARD_EVENT_VIEWS, {
    variables: {
      fetchPolicy: "network-first",
      input: {
        event_id: eventId,
        teacher_id: teacherId,
        filter,
        search_term: searchTerm,
      },
      page: currentPage,
      perPage: 50,
    },
  })

  const handleClearSearch = () => {
    searchInputRef.current.value = ""
    setSearchTerm("")
  }

  if (error) return <ErrorPlaceholder caption="Error loading event views" error={error} />

  return (
    <div>
      <SearchInput
        classNames="mb-6"
        debounceMilliSeconds={300}
        placeholder="Search by name / email"
        handleChange={searchTerm => setSearchTerm(searchTerm)}
        handleClear={() => setSearchTerm("")}
        innerRef={searchInputRef}
        value={searchTerm}
      />

      {loading && <LoadingPlaceholder caption="Loading event views..." icon={PresentationChartLineIcon} />}

      {!loading && data.eventUserViews.data.length === 0 && (
        <SimplePlaceholder icon={PresentationChartLineIcon} title="No event views found">
          <p className="mt-1 text-sm text-gray-500">
            No event views found.{" "}
            {searchTerm.length > 0 && (
              <span>
                Try a different search term.{" "}
                <Button className="mt-4" color="primary" fullWidth onPress={() => handleClearSearch()}>
                  Clear Search
                </Button>
              </span>
            )}
          </p>
        </SimplePlaceholder>
      )}

      <ul className="clear-right">
        {!loading &&
          data.eventUserViews.data.map(view => (
            <li key={view.user_id}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-md">{view.name}</span>

                <div>
                  <Chip
                    color="warning"
                    classNames={{
                      base: "mr-2",
                      content: "text-white text-xs font-semibold",
                    }}
                  >
                    {humanReadableDuration(view.last_viewed_at)} ago
                  </Chip>

                  <Chip
                    classNames={{
                      content: "text-white text-xs font-semibold",
                      base: `${view.type === "Staff" ? 'bg-sky-400' : 'bg-green-400'}`,
                    }}
                  >
                    {view.type}
                  </Chip>
                </div>
              </div>

              {view.email && <span className="text-sm text-content2-foreground">{view.email}</span>}

              <div className="flex items-center mt-1 text-sm text-gray-600 dark:text-content2-foreground">
                <ComputerDesktopIcon className="w-5 h-5 mr-2" />
                <span className="mr-4">{view.platform_family}</span>
                <DeviceTabletIcon className="w-5 h-5 mr-2" />
                <span className="mr-4">{view.device_type}</span>
                <WindowIcon className="w-5 h-5 mr-2" />
                {view.browser_family}
              </div>

              <Divider className="my-3" />
            </li>
          ))}
      </ul>

      {data?.eventUserViews?.paginatorInfo?.lastPage > 1 && (
        <div className="flex items-center justify-between w-full mt-3">
          <PaginatorInfo paginatorInfo={data?.eventUserViews?.paginatorInfo}></PaginatorInfo>

          <Pagination
            isCompact
            showControls
            showShadow
            color="secondary"
            page={currentPage}
            total={data?.eventUserViews.paginatorInfo?.lastPage}
            onChange={page => setCurrentPage(page)}
          />
        </div>
      )}

      {endSlot && (
        <div className="mt-4">{endSlot}</div>
      )}
    </div>
  )
}

EventViewsList.propTypes = {
  endSlot: PropTypes.node,
  eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  filter: PropTypes.string,
  teacherId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

export default EventViewsList
